import {
  ActivityFilterKey,
  useAssetIntegrationReadings,
  useWorkspaceAsset,
} from '@gripp/shared-logic';
import {
  ActivityFilterScreen,
  ActivityFilterScreenHandle,
  ActivityList,
  type ColumnData,
  SlideAction,
  SlideLayout,
  type SlideLayoutLocationState,
  useSlideLayout,
  ViewMode,
} from '@gripp/shared-ui';
import { useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { View } from 'react-native';
import { ActivityFilters } from '../../activity/components/activityFilters';
import { HeaderResetButton } from '../../shared/components/headerResetButton';
import { AssetIntegrationReadingsSection } from './assetIntegrationReadingsSection';
import { AssetRouteProvider, useAssetRouting } from '../assetRouteContext';
import { ALL_ACTIVITIES_QUERY_KEY, isActivitiesParent } from '../assetUtils';
import { AssetCard } from './assetCard';
import { AssetFilters } from './assetFilters';

export const AssetDetail = () => {
  const { assetId, issueId, digestKey, workspaceId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const filterRef = useRef<ActivityFilterScreenHandle>(null);
  const { workspaceAsset } = useWorkspaceAsset(assetId ?? '', !assetId);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [count, setCount] = useState<number>();
  // The asset can be reached from four contexts: standalone (`/assets/...`),
  // an activity conversation (`/activities/:issueId/asset/...`), the
  // integration digest panel (`/activities/digest/:digestKey/asset/...`), or a
  // workspace (`/workspaces/:workspaceId/asset/...`). The digest case
  // (GRIPP #2887) keeps the digest prefix so back returns to the digest panel
  // rather than /assets. `digestKey` is already URL-encoded here (carries the
  // `%7C` pipe) — pass it through untouched so the prefix round-trips.
  const isActivitiesContext = Boolean(issueId) || Boolean(digestKey);
  const assetBasePath = workspaceId
    ? `/workspaces/${workspaceId}/asset/${assetId}`
    : digestKey
      ? `/activities/digest/${digestKey}/asset/${assetId}`
      : issueId
        ? `/activities/${issueId}/asset/${assetId}`
        : `/assets/${assetId}`;
  const locationState = location.state as SlideLayoutLocationState | null;
  const initialTransition =
    locationState?.slideLayoutInitialTransition === SlideAction.NEXT ||
    locationState?.slideLayoutInitialTransition === SlideAction.PREV
      ? locationState.slideLayoutInitialTransition
      : undefined;

  if (!assetId) {
    return null;
  }

  const leftColumn = {
    title: workspaceId ? 'admin.menu.workspaces' : 'assets.menu.title',
    mainComponent: (
      <AssetRouteProvider
        value={{
          basePath: assetBasePath,
          listPath: workspaceId
            ? '/workspaces'
            : isActivitiesContext
            ? '/activities'
            : '/assets',
          isWorkspaceMode: !!workspaceId,
          issueId,
        }}
      >
        <AssetCard assetId={assetId} />
      </AssetRouteProvider>
    ),
    headerComponent: <AssetFilters assetId={assetId} />,
    showBackButton: true,
    backButtonTransition: SlideAction.PREV,
    backButtonTransitionBehavior: 'concurrent',
    backButtonTransitionExitColumn: 'both',
    onBackButtonPress: () =>
      navigate(workspaceId || isActivitiesContext ? '../../../..' : '../..', {
        relative: 'path',
        state: { slideLayoutInitialTransition: SlideAction.PREV },
      }),
    disableScroll: false,
  };

  const rightColumn: ColumnData = {
    title: workspaceId ? 'admin.menu.workspaces' : 'assets.menu.title',
    rawTitle: workspaceAsset?.asset?.name,
    count,
    mainComponent: <RightColumn assetId={assetId} onCountChange={setCount} />,
    headerComponent: (
      <ActivityFilters
        assetId={assetId}
        showAddButton={false}
        onFilterChange={() => setIsFilterPanelOpen(true)}
      />
    ),
    showBackButton: true,
    disableScroll: true,
    onBackButtonPress: () => {
      navigate(workspaceId ? '../../../..' : '../..', {
        relative: 'path',
        state: { slideLayoutInitialTransition: SlideAction.PREV },
      });
    },
    backButtonTransition: SlideAction.PREV,
    backButtonTransitionBehavior: 'concurrent',
  };

  const thirdColumn = isFilterPanelOpen
    ? {
        title: 'assets.menu.title',
        mainComponent: (
          <ActivityFilterScreen
            ref={filterRef}
            mode="page"
            isVisible={true}
            filterKey={ActivityFilterKey.AssetActivity}
            assetId={assetId}
            bottomBar={() => null}
          />
        ),
        headerComponent: (
          <HeaderResetButton onReset={() => filterRef.current?.onReset()} />
        ),
        showBackButton: true,
        disableScroll: true,
      }
    : undefined;

  return (
    <SlideLayout
      leftColumn={leftColumn}
      rightColumn={rightColumn}
      thirdColumn={thirdColumn}
      viewMode={ViewMode.DUAL}
      initialTransition={initialTransition}
    />
  );
};

const RightColumn = ({
  assetId,
  onCountChange,
}: {
  assetId: string;
  onCountChange?: (count: number) => void;
}) => {
  const { prepareSlideTransition } = useSlideLayout();
  const navigate = useNavigate();
  const location = useLocation();
  const { basePath } = useAssetRouting(assetId);
  // Read the context params from the route (present on
  // activities/:issueId/asset/:assetId/activity/all and the digest equivalent
  // activities/digest/:digestKey/asset/:assetId/activity/all). digestKey is
  // URL-encoded here (carries the %7C pipe) — pass it through untouched.
  const { issueId: contextIssueId, digestKey: contextDigestKey } = useParams();
  // Shares the readings section's query cache — used only to suppress the
  // contradictory "No activity" empty state when readings render above.
  const { readings } = useAssetIntegrationReadings(assetId);

  const onIssueSelected = async (issueId: string) => {
    // Use the outer context param (digest key or issue ID) for the first
    // segment, not the newly-selected issueId — otherwise the original context
    // is lost and back would bounce out of the digest/conversation chain.
    const url =
      isActivitiesParent(location) && contextDigestKey
        ? `/activities/digest/${contextDigestKey}/asset/${assetId}/activity/${issueId}?${ALL_ACTIVITIES_QUERY_KEY}`
        : isActivitiesParent(location) && contextIssueId
          ? `/activities/${contextIssueId}/asset/${assetId}/activity/${issueId}?${ALL_ACTIVITIES_QUERY_KEY}`
          : `${basePath}/activity/${issueId}?${ALL_ACTIVITIES_QUERY_KEY}`;
    prepareSlideTransition(SlideAction.NEXT);
    navigate(url, {
      state: { slideLayoutInitialTransition: SlideAction.NEXT },
    });
  };

  return (
    <View style={{ flex: 1 }}>
      <AssetIntegrationReadingsSection assetId={assetId} />
      <ActivityList
        filterKey={ActivityFilterKey.AssetActivity}
        onIssueSelected={onIssueSelected}
        assetId={assetId}
        showAssetName={false}
        showImage={false}
        pageSize={50}
        showTopBar={false}
        hideTextEmpty={readings.length > 0}
        onActivityCountChange={onCountChange}
      />
    </View>
  );
};
